import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Component, type ReactNode } from "react";
import { Navigate, RouterProvider, createBrowserRouter } from "react-router-dom";
import "../editor/monacoSetup";
import { AnalysisPage } from "../features/analysis/AnalysisPage";
import { DerivedPage } from "../features/derived/DerivedPage";
import { FlightsPage } from "../features/flights/FlightsPage";
import { FramePage } from "../features/frame/FramePage";
import { InputsPage } from "../features/inputs/InputsPage";
import { LivePage } from "../features/live/LivePage";
import { AdminPage } from "../features/platform/AdminPage";
import { SubscriptionsPage } from "../features/platform/SubscriptionsPage";
import "../features/platform/platform.css";
import { ReportsPage } from "../features/reports/ReportsPage";
import { RunsPage } from "../features/runs/RunsPage";
import { TriggersPage } from "../features/triggers/TriggersPage";
import { TestsPage } from "../features/verify/TestsPage";
import { VersionsPage } from "../features/versions/VersionsPage";
import { Shell } from "./Shell";

const queryClient = new QueryClient({
  defaultOptions: { queries: { refetchOnWindowFocus: false, retry: 1 } },
});

class Boundary extends Component<{ children: ReactNode }, { error: Error | null }> {
  state = { error: null as Error | null };
  static getDerivedStateFromError(error: Error) {
    return { error };
  }
  render() {
    if (this.state.error) {
      return (
        <div className="empty">
          <h3>Something went wrong</h3>
          <pre className="mono small" style={{ textAlign: "left", whiteSpace: "pre-wrap" }}>
            {this.state.error.message}
          </pre>
          <button className="btn" onClick={() => this.setState({ error: null })}>
            Retry
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

const wrap = (el: ReactNode) => <Boundary>{el}</Boundary>;

const router = createBrowserRouter([
  {
    path: "/",
    element: <Shell />,
    children: [
      { index: true, element: <Navigate to="/flights" replace /> },
      { path: "flights", element: wrap(<FlightsPage />) },
      { path: "frame", element: wrap(<FramePage />) },
      { path: "derived", element: wrap(<DerivedPage />) },
      { path: "inputs", element: wrap(<InputsPage />) },
      { path: "triggers", element: wrap(<TriggersPage />) },
      { path: "reports", element: wrap(<ReportsPage />) },
      { path: "analysis", element: wrap(<AnalysisPage />) },
      { path: "runs", element: wrap(<RunsPage />) },
      { path: "tests", element: wrap(<TestsPage />) },
      { path: "versions", element: wrap(<VersionsPage />) },
      { path: "live", element: wrap(<LivePage />) },
      { path: "subscriptions", element: wrap(<SubscriptionsPage />) },
      { path: "admin", element: wrap(<AdminPage />) },
      { path: "*", element: <Navigate to="/flights" replace /> },
    ],
  },
]);

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
    </QueryClientProvider>
  );
}
